import './style.scss';

import classnames from 'classnames/dedupe';
import csv from 'csvtojson';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import type { Accept, DropzoneRef, DropzoneState } from 'react-dropzone';
import Dropzone from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import Icon from '@/components/icon';
import type { UploadStatus } from '@/utils/imageUtils';
import { uploadToS3 } from '@/utils/imageUtils';

interface ComponentDropzoneProps {
  files?: ExtendedFile[];
  onChange?: (files: ExtendedFile[]) => void;
  accept?: Accept;
  uploadToS3?: boolean;
  path?: string;
  onAdded?: (data: any) => void;
  removeOnAdd?: boolean;
  onDrop?: (file: File) => void;
  getJson?: (json: any[]) => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  id?: string;
  style?: React.CSSProperties;
  showOverlayOptions?: boolean;
  showUploadStatus?: boolean;
  imagePreviewStyle?: React.CSSProperties;
  allowRemove?: boolean;
  hideIcon?: boolean;
  hideDropText?: boolean;
  dropText?: string;
}

// Extend the native File type with a preview URL.
type ExtendedFile = File & { preview: string };

function fileSizeToHumanReadable(size: number): string {
  const i = Math.floor(Math.log(size) / Math.log(1024));
  // @ts-ignore
  return `${(size / 1024 ** i).toFixed(2) * 1} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
}

const ComponentDropzone = forwardRef<DropzoneRef, ComponentDropzoneProps>(
  (props, ref) => {
    const [files, setFiles] = useState<ExtendedFile[]>([]);
    const progressBar = useRef<HTMLDivElement>(null);
    const skipInitialChange = useRef(true);

    useEffect(() => {
      if (skipInitialChange.current) {
        skipInitialChange.current = false;
        return;
      }

      props.onChange?.(files);
    }, [files]);

    useEffect(() => {
      if (props.files) {
        setFiles(props.files);
      }
    }, [props.files]);

    // Update the progress bar's width.
    const onProgress = (progress: number) => {
      if (progressBar.current) {
        progressBar.current.style.width = `${progress}%`;
      }
    };

    // Remove a file from the state.
    const removeFile = (file: ExtendedFile) => {
      const newFiles = [...files];
      const index = newFiles.indexOf(file);
      if (index > -1) {
        newFiles.splice(index, 1);
        setFiles(newFiles);
      }
    };

    return (
      <Dropzone
        ref={ref}
        accept={
          props.accept || {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp'],
          }
        }
        onDrop={(newFiles: File[]) => {
          // Update files state and trigger onChange.
          setFiles((prevFiles) => {
            const updatedFiles: ExtendedFile[] = [
              ...prevFiles,
              ...newFiles.map((f) => {
                return Object.assign(f, { preview: URL.createObjectURL(f) });
              }),
            ];
            console.log('New files dropped:', updatedFiles);
            return updatedFiles;
          });

          console.debug('Dropped files:', newFiles);

          newFiles.forEach((file) => {
            console.log(file);
            if (props.uploadToS3) {
              if (props.path) {
                const uuid = uuidv4();
                const filename = `${uuid}-${file.name}`;
                console.debug('Uploading', filename);
                uploadToS3(
                  file,
                  props.path,
                  filename,
                  uuid,
                  null,
                  (status: UploadStatus) => onProgress(status.progress)
                )
                  .then((res) => {
                    if (typeof props.onAdded === 'function') {
                      props.onAdded(res.data);
                    }
                    if (props.removeOnAdd) {
                      // Assert file as ExtendedFile.
                      removeFile(file as ExtendedFile);
                    }
                  })
                  .catch((err) => {
                    console.error('Error uploading media to server...');
                    console.error(err);
                  });
              } else {
                console.error(
                  'Dropzone error: Please specify the `path` in props.'
                );
              }
            }
            if (typeof props.onDrop === 'function') {
              props.onDrop(file);
            }
            if (
              file.type === 'text/csv' ||
              (file.type === 'application/vnd.ms-excel' &&
                file.name.toLowerCase().endsWith('.csv'))
            ) {
              const reader = new FileReader();
              reader.onload = () => {
                const fileAsBinaryString = reader.result as string;
                csv({ noheader: true, output: 'json' })
                  .fromString(fileAsBinaryString)
                  .then((csvRows) => {
                    const toJson: any[] = [];
                    csvRows.forEach((aCsvRow, i) => {
                      if (i !== 0) {
                        const builtObject: { [key: string]: any } = {};
                        Object.keys(aCsvRow).forEach((aKey) => {
                          builtObject[csvRows[0][aKey]] = aCsvRow[aKey];
                        });
                        toJson.push(builtObject);
                      }
                    });
                    if (typeof props.getJson === 'function') {
                      props.getJson(toJson);
                    }
                  });
              };
              reader.onabort = () => console.log('File reading was aborted');
              reader.onerror = () => console.log('File reading has failed');
              reader.readAsText(file, 'ISO-8859-1');
            }
          });
        }}
      >
        {(data: DropzoneState) => {
          const rootProps = data.getRootProps({
            onClick: props.onClick
              ? (e) => {
                  e.stopPropagation();
                  props.onClick!(e);
                }
              : undefined,
          });
          const inputProps = data.getInputProps();

          return (
            <div
              id={props.id || 'dropzone'}
              className={classnames(
                'rui-dropzone',
                data.isDragActive ? 'rui-dropzone-is-active' : ''
              )}
              {...rootProps}
              style={props.style || {}}
            >
              <input {...inputProps} />
              {files && files.length ? (
                files.map((fileData) => (
                  <div key={fileData.name} className="rui-dropzone-preview">
                    {props.showOverlayOptions && (
                      <div className="image-overlay">
                        <Icon
                          name="trash"
                          className="delete-image-icon"
                          style={{ cursor: 'pointer' }}
                          // Delete icon onClick handler omitted for brevity.
                        />
                        <Icon
                          name="maximize2"
                          className="expand-icon"
                          // Expand icon onClick handler omitted for brevity.
                        />
                      </div>
                    )}
                    <img
                      data-dz-thumbnail="true"
                      src={fileData.preview || ''}
                      alt={fileData.name}
                      style={props.imagePreviewStyle || {}}
                    />
                    {props.showUploadStatus && (
                      <div className="rui-dropzone-details">
                        <div className="rui-dropzone-size">
                          <span>{fileSizeToHumanReadable(fileData.size)}</span>
                        </div>
                        <div className="rui-dropzone-filename">
                          <span>{fileData.name}</span>
                        </div>
                        <div className="progress">
                          <div
                            ref={progressBar}
                            className="progress-bar progress-bar-primary"
                            role="progressbar"
                          >
                            <span className="progress-text" />
                          </div>
                        </div>
                      </div>
                    )}
                    {props.allowRemove && (
                      <button
                        className="rui-dropzone-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(fileData);
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="rui-dropzone-message" style={props.style || {}}>
                  {!props.hideIcon && (
                    <span className="rui-dropzone-icon">
                      <Icon name="upload-cloud" />
                    </span>
                  )}
                  <span className="rui-dropzone-text">
                    {!props.hideDropText &&
                      (props.dropText || 'Drop file here or click to upload.')}
                  </span>
                </div>
              )}
            </div>
          );
        }}
      </Dropzone>
    );
  }
);

export default ComponentDropzone;
